import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import React from 'react';
import { Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { jrLogoColorSvg } from '../assets/images';
import { ResponsiveMenu } from '../components/ResponsiveMenu/ResponsiveMenu';
import { SITE_LINKS } from '../constants';
import { useScreenSize } from '../hooks/use-screen-size';
import { Button } from './Button';

export interface HeaderProps {
  type?: 'default' | 'logo-only';
  user?: { name?: string; imgSrc?: string };
  menuItems?: { title: string; href: string }[];
  userDropdownItems?: { title: string; href: string }[];
  isReferralPageVisible?: boolean;
  activeItem?: string;
  logoHref?: string;
}

export const Header: React.FC<HeaderProps> = ({
  type = 'default',
  isReferralPageVisible = false,
  user,
  activeItem = '',
  menuItems,
  userDropdownItems,
  logoHref,
}) => {
  const __ = useTranslation().t;
  const { width, screenSizes } = useScreenSize();

  const isSmallScreen = width <= screenSizes.lg;
  const [isResponsiveMenuVisible, setIsResponsiveMenuVisible] = React.useState(false);

  React.useEffect(() => {
    if (!isSmallScreen) {
      setIsResponsiveMenuVisible(false);
    }
  }, [isSmallScreen]);

  if (type === 'logo-only') {
    return <HeaderLogoOnly />;
  }

  // NOTE: This might be used in the future
  // const UserProfilePicture = () => {
  //   return <img src={user?.imgSrc || userPlaceholderSvg} alt="User" />;
  // };

  return (
    <div className="oejr-navbar-wrapper">
      <ResponsiveMenu
        isVisible={isResponsiveMenuVisible}
        menuItems={menuItems}
        onClose={() => setIsResponsiveMenuVisible(false)}
      />

      <div className="oejr-navbar container">
        <div className="oejr-navbar-brand">
          <a href={logoHref}>
            <img src={jrLogoColorSvg} alt="Logo" />
          </a>
        </div>
        <div className="oejr-navbar-control">
          <div className="oejr-menu-xs d-inline-block d-lg-none">
            <button className="oejr-menu-button" onClick={() => setIsResponsiveMenuVisible(true)}>
              <span className="oejr-items-dropdown-title">{__('menu')}</span>
              <span>
                <FontAwesomeIcon icon={'fa fa-bars' as IconProp} />
              </span>
            </button>
          </div>
          <ul className="oejr-navbar-control-buttons d-none d-lg-inline-block">
            {isReferralPageVisible && (
              <li>
                <Button
                  className="oejr-navbar-referral-button"
                  size="sm"
                  href={SITE_LINKS.Share}
                  label={__('referral.headerButton')}
                  icon="gift"
                />
              </li>
            )}
            {menuItems?.map((item, key) => (
              <li
                className={classNames(
                  item.title === activeItem && 'oejr-navbar-control-button-active',
                )}
                key={key}
              >
                <a href={item.href}>{item.title}</a>
              </li>
            ))}
          </ul>
          <div className="oejr-navbar-control-divider d-none d-lg-inline-block" />
          <ul className="oejr-navbar-control-user d-none d-lg-inline-block">
            {!!userDropdownItems?.length && (
              <li>
                <Dropdown className="oejr-navbar-dropdown">
                  <Dropdown.Toggle as={CustomDropdown}>
                    <span className="oejr-navbar-control-user-name">{user?.name}</span>
                    <span className="d-none d-lg-inline-block">
                      <FontAwesomeIcon icon={'fa fa-chevron-down' as IconProp} />
                    </span>
                    <span className="d-inline-block d-lg-none">
                      <FontAwesomeIcon icon={'fa fa-caret-down' as IconProp} />
                    </span>
                    {/* <UserProfilePicture /> */}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {userDropdownItems?.map((item, key) => (
                      <Dropdown.Item href={item.href} active={activeItem === item.title} key={key}>
                        {item.title}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

const HeaderLogoOnly = () => (
  <div className="oejr-navbar-wrapper">
    <div className="oejr-navbar oejr-navbar-logo-only container">
      <div className="oejr-navbar-brand">
        <img src={jrLogoColorSvg} alt="Logo" />
      </div>
    </div>
  </div>
);

type CustomToggleProps = {
  children?: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
};

const CustomDropdown = React.forwardRef(
  ({ children, onClick }: CustomToggleProps, ref: React.Ref<HTMLAnchorElement>) => (
    <a
      className="cursor-pointer"
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick && onClick(e);
      }}
    >
      {children}
    </a>
  ),
);
